import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomSelect,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../common';
import { SOURCE_TYPE_LABELS } from '../constants/supplyOrdersConstants';
import {
  buildGroupsFromExternalOrderItems,
  buildQuantityOnlyProviderGroups,
  formatSupplyOrderQuantity,
  getMissingExternalOrderItems,
  getSupplyOrderApiErrorMessage,
} from '../utils/supplyOrdersUtils';
import { getArticleLabel } from '../../internal-orders/utils/internalOrdersUtils';
import { getProviderDisplayLabel } from '../utils/supplyOrdersUtils';
import { useInventoryArticlesByUuids } from '../../internal-orders/hooks/useInventoryArticlesByUuids';
import { guardDialogClose } from '../../utils/actionLockUtils';

const GenerateSupplyOrdersDialog = ({
  open,
  onClose,
  externalOrder,
  warehouses = [],
  providers = [],
  onGenerate,
  isProcessing = false,
  providerOnly = false,
}) => {
  const [destinationWarehouse, setDestinationWarehouse] = useState('');
  const [issueNow, setIssueNow] = useState(false);
  const [rows, setRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultOrders, setResultOrders] = useState([]);

  const missingItems = useMemo(
    () => getMissingExternalOrderItems(externalOrder),
    [externalOrder]
  );

  const articleUuids = useMemo(
    () => missingItems.map((item) => item.uuid_inventory_article),
    [missingItems]
  );

  const { articles: inventoryArticles } = useInventoryArticlesByUuids(
    articleUuids,
    open && missingItems.length > 0
  );

  const articlesByUuid = useMemo(
    () =>
      inventoryArticles.reduce((acc, article) => {
        if (article?.uuid) acc[article.uuid] = article;
        return acc;
      }, {}),
    [inventoryArticles]
  );

  useEffect(() => {
    if (open && externalOrder) {
      setDestinationWarehouse(externalOrder.uuid_destination_warehouse || '');
      setIssueNow(false);
      setResultOrders([]);
      setErrorMessage('');
      setRows(
        missingItems.map((item) => ({
          selected: true,
          uuid_external_order_item: item.uuid,
          uuid_inventory_article: item.uuid_inventory_article,
          missing_quantity: item.missing_quantity,
          source_type: 'provider',
          uuid_provider_legal_entity: '',
          uuid_source_warehouse: '',
          provider_delivery_commitment_at: '',
          quantity: item.missing_quantity,
        }))
      );
    }
  }, [open, externalOrder, missingItems]);

  const sourceTypeOptions = Object.entries(SOURCE_TYPE_LABELS)
    .filter(([value]) => !providerOnly || value === 'provider')
    .map(([value, label]) => ({
      value,
      label,
    }));

  const handleRowChange = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
              ...(field === 'source_type'
                ? {
                    uuid_provider_legal_entity: '',
                    uuid_source_warehouse: '',
                    provider_delivery_commitment_at: '',
                  }
                : {}),
            }
          : row
      )
    );
  };

  const handleGenerate = async () => {
    const groups = providerOnly
      ? buildQuantityOnlyProviderGroups(rows)
      : buildGroupsFromExternalOrderItems(rows);

    if (groups.length === 0) {
      setErrorMessage('Seleccioná al menos un artículo con cantidad válida.');
      return;
    }

    if (!providerOnly) {
      for (const group of groups) {
        if (group.source_type === 'provider' && !group.uuid_provider_legal_entity) {
          setErrorMessage('Seleccioná un proveedor para cada grupo de artículos.');
          return;
        }
        if (group.source_type === 'warehouse' && !group.uuid_source_warehouse) {
          setErrorMessage('Seleccioná un depósito origen para cada grupo de artículos.');
          return;
        }
      }
    }

    const payload = {
      issue_now: providerOnly ? false : issueNow,
      groups,
    };

    if (destinationWarehouse) {
      payload.uuid_destination_warehouse = destinationWarehouse;
    }

    try {
      setErrorMessage('');
      const createdOrders = await onGenerate({
        externalOrderId: externalOrder.uuid,
        payload,
      });
      setResultOrders(Array.isArray(createdOrders) ? createdOrders : [createdOrders]);
    } catch (error) {
      setErrorMessage(
        getSupplyOrderApiErrorMessage(error, 'No se pudieron generar las órdenes de abastecimiento')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer
      open={open}
      onClose={guardDialogClose(isProcessing, onClose)}
      size="large"
      minHeight="60vh"
    >
      <DialogTitle>
        {providerOnly ? 'Generar borrador a proveedor' : 'Generar órdenes de abastecimiento'}
      </DialogTitle>
      <DialogBody>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          {resultOrders.length > 0 ? (
            <Alert severity="success">
              Se generaron {resultOrders.length} orden(es):
              <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                {resultOrders.map((order) => (
                  <li key={order.uuid}>
                    {order.order_code || 'Borrador'} — {order.status}
                  </li>
                ))}
              </Box>
            </Alert>
          ) : (
            <>
              {providerOnly && (
                <Alert severity="info">
                  Se generará un borrador con las cantidades indicadas. Compras asignará proveedor
                  y compromiso por material al emitir la orden.
                </Alert>
              )}

              <CustomSelect
                label="Depósito destino"
                name="uuid_destination_warehouse"
                value={destinationWarehouse}
                onChange={(event) => setDestinationWarehouse(event.target.value)}
                fullWidth
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.uuid} value={warehouse.uuid}>
                    {warehouse.name || warehouse.code}
                  </MenuItem>
                ))}
              </CustomSelect>

              {!providerOnly && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={issueNow}
                    onChange={(event) => setIssueNow(event.target.checked)}
                  />
                }
                label="Emitir inmediatamente"
              />
              )}

              <Typography variant="subtitle1" fontWeight={700}>
                Artículos con faltante
              </Typography>

              <GeneralTableBox>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <HeadTableCell />
                      <HeadTableCell>Artículo</HeadTableCell>
                      <HeadTableCell>Faltante</HeadTableCell>
                      <HeadTableCell>Cantidad</HeadTableCell>
                      {!providerOnly && (
                        <>
                          <HeadTableCell>Compromiso</HeadTableCell>
                          <HeadTableCell>Origen</HeadTableCell>
                          <HeadTableCell>Proveedor / Depósito</HeadTableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <RowTableCell colSpan={providerOnly ? 4 : 7} sx={{ textAlign: 'center', py: 3 }}>
                          No hay artículos con cantidades faltantes
                        </RowTableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, index) => {
                        return (
                          <TableRow key={row.uuid_external_order_item}>
                            <RowTableCell>
                              <Checkbox
                                checked={row.selected}
                                onChange={(event) =>
                                  handleRowChange(index, 'selected', event.target.checked)
                                }
                              />
                            </RowTableCell>
                            <RowTableCell>
                              {getArticleLabel(articlesByUuid, row.uuid_inventory_article)}
                            </RowTableCell>
                            <RowTableCell>
                              {formatSupplyOrderQuantity(row.missing_quantity)}
                            </RowTableCell>
                            <RowTableCell>
                              <CustomTextField
                                type="number"
                                value={row.quantity}
                                onChange={(event) =>
                                  handleRowChange(index, 'quantity', event.target.value)
                                }
                                inputProps={{
                                  min: 0,
                                  max: row.missing_quantity,
                                  step: '0.001',
                                }}
                                size="small"
                                sx={{ maxWidth: 100 }}
                              />
                            </RowTableCell>
                            {!providerOnly && (
                              <>
                            <RowTableCell>
                              {row.source_type === 'provider' ? (
                                <CustomTextField
                                  type="date"
                                  value={row.provider_delivery_commitment_at}
                                  onChange={(event) =>
                                    handleRowChange(
                                      index,
                                      'provider_delivery_commitment_at',
                                      event.target.value
                                    )
                                  }
                                  InputLabelProps={{ shrink: true }}
                                  size="small"
                                  sx={{ minWidth: 150 }}
                                />
                              ) : (
                                '-'
                              )}
                            </RowTableCell>
                            <RowTableCell>
                              <CustomSelect
                                name="source_type"
                                value={row.source_type}
                                onChange={(event) =>
                                  handleRowChange(index, 'source_type', event.target.value)
                                }
                                size="small"
                              >
                                {sourceTypeOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </CustomSelect>
                            </RowTableCell>
                            <RowTableCell>
                              {row.source_type === 'provider' ? (
                                <CustomSelect
                                  name="uuid_provider_legal_entity"
                                  value={row.uuid_provider_legal_entity}
                                  onChange={(event) =>
                                    handleRowChange(
                                      index,
                                      'uuid_provider_legal_entity',
                                      event.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  {providers.map((provider) => (
                                    <MenuItem key={provider.uuid} value={provider.uuid}>
                                      {getProviderDisplayLabel(provider)}
                                    </MenuItem>
                                  ))}
                                </CustomSelect>
                              ) : (
                                <CustomSelect
                                  name="uuid_source_warehouse"
                                  value={row.uuid_source_warehouse}
                                  onChange={(event) =>
                                    handleRowChange(
                                      index,
                                      'uuid_source_warehouse',
                                      event.target.value
                                    )
                                  }
                                  size="small"
                                >
                                  {warehouses.map((warehouse) => (
                                    <MenuItem key={warehouse.uuid} value={warehouse.uuid}>
                                      {warehouse.name || warehouse.code}
                                    </MenuItem>
                                  ))}
                                </CustomSelect>
                              )}
                            </RowTableCell>
                              </>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </GeneralTableBox>
            </>
          )}
        </Box>
      </DialogBody>
      <DialogActions
        handleClose={onClose}
        handleSave={resultOrders.length > 0 ? onClose : handleGenerate}
        saveLabel={
          resultOrders.length > 0 ? 'Cerrar' : providerOnly ? 'Generar borrador' : 'Generar órdenes'
        }
        saveButtonProps={{ isLoading: isProcessing }}
      />
    </DialogContainer>
  );
};

export default GenerateSupplyOrdersDialog;
